import './index.scss'

import { useRef, useState } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

import { useAuth } from '@auth/AuthProvider'
import Modal from '@components/Modal'
import { useChurchDataProvider } from '@data/ChurchDataProvider'
import SeriesController from '@data/controller/SeriesController'
import DateButton from '../DateButton'
import { ChurchSeries, ChurchSeriesAttributesBody } from '@interfaces/series.interface'
import { isoTimestamp } from '@utility/date'
import { uploadLibraryThumbnail } from '@utility/libraryThumbnail'
import { localizedValue } from '@utility/localizedText'

import closeIcon from 'url:@icon/close.svg';

// Creates a series, or edits one when `series` is given. Styled after the
// Staff and team editors. The caller keys it per open, so each open starts
// from the series as it is now; `initialCampusID` and `initialCategoryID`
// seed a new one from wherever it was started.
export default function SeriesEditorModal(props: {
  isOpen: boolean,
  series?: ChurchSeries | null,
  initialCampusID?: string | null,
  initialCategoryID?: string | null,
  close: () => void,
  onSaved: (series: ChurchSeries) => void
}) {
  const auth = useAuth();
  const { campuses, categories } = useChurchDataProvider();
  const existing = props.series?.attributes;
  const isEditing = Boolean(props.series);

  // Only a campus and category that still exist are carried in, so a stale
  // link can't preset one the pickers don't offer.
  const initialCampusID = campuses.some((campus) => campus.id === props.initialCampusID) ?
    props.initialCampusID :
    (campuses.length === 1 ? campuses[0].id : null);
  const initialCategoryID = categories.some((category) => category.id === props.initialCategoryID) ? props.initialCategoryID : null;

  const [name, setName] = useState(existing?.name ?? '');
  const [campusID, setCampusID] = useState(existing?.campusID ?? initialCampusID ?? '');
  const [categoryID, setCategoryID] = useState(existing?.categoryID ?? initialCategoryID ?? '');
  // Series are listed newest first by this. A new one starts today, the
  // likeliest start for one being set up.
  const [startDate, setStartDate] = useState<Date | null>(() => {
    if (existing?.startDate) {
      const stored = new Date(existing.startDate);
      return Number.isNaN(stored.getTime()) ? null : stored;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  // The thumbnail and its hash travel together, so one never describes the
  // other's image.
  const [thumbnail, setThumbnail] = useState<{ url: string; blurHash: string | null } | null>(
    existing?.thumbnailURL ? { url: existing.thumbnailURL, blurHash: existing.thumbnailBlurHash } : null
  );

  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sortedCampuses = [...campuses].sort((one, two) => one.attributes.name.localeCompare(two.attributes.name));
  const sortedCategories = [...categories].sort((one, two) => one.name.localeCompare(two.name));

  const trimmedName = name.trim();
  const isBusy = isSaving || isUploading;
  const canSave = trimmedName.length > 0 && campusID.length > 0 && startDate !== null && !isBusy;

  const upload = async (file: File) => {
    if (!auth.churchId) return;

    setIsUploading(true);
    setError(null);

    try {
      const result = await uploadLibraryThumbnail(auth.churchId, file, 'series', auth);

      if (!result.thumbnail) {
        setError(result.error ?? "The thumbnail couldn't be uploaded. Please try again.");
        return;
      }

      setThumbnail(result.thumbnail);
    } finally {
      setIsUploading(false);
    }
  };

  const save = async () => {
    if (!auth.churchId) {
      setError('Missing church context. Please refresh and try again.');
      return;
    }

    if (!canSave || !startDate) return;

    setIsSaving(true);
    setError(null);

    const attributes: ChurchSeriesAttributesBody = {
      campusID,
      categoryID: categoryID || null,
      startDate: isoTimestamp(startDate),
      // An edit replaces only the translation shown, keeping the others.
      name: localizedValue(existing?.name ?? null, existing?.nameLocalizations, trimmedName) ?? trimmedName,
      thumbnailURL: thumbnail?.url ?? null,
      thumbnailBlurHash: thumbnail?.blurHash ?? null
    };

    try {
      const response = props.series ?
        await SeriesController.updateSeries(auth.churchId, props.series.id, attributes, auth) :
        await SeriesController.createSeries(auth.churchId, attributes, auth);

      if (response.error || !response.body) {
        setError(friendlySaveError(response.error || `Unable to ${isEditing ? 'save' : 'create'} this series right now.`));
        return;
      }

      props.onSaved(response.body);
    } finally {
      setIsSaving(false);
    }
  };

  let submitTitle = isEditing ? 'Save Changes' : 'Create Series';
  if (isSaving) {
    submitTitle = isEditing ? 'Saving...' : 'Creating...';
  }

  return <Modal
    id='series-editor-modal'
    isOpen={props.isOpen}
    footerButtons={[]}>
    <div className='series-editor-modal-content'>
      <div className='header'>
        <button
          type='button'
          className='header-control close'
          disabled={isBusy}
          onClick={props.close}>
          <img src={closeIcon} alt='Close icon' />
        </button>

        <h3 className='title'>{isEditing ? 'Edit Series' : 'New Series'}</h3>

        <span className='header-control-spacer' />
      </div>

      <form
        className='series-form'
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}>
        {error ? <p className='save-error'>{error}</p> : null}

        <div className='section'>
          <div className='input'>
            <input
              type='text'
              placeholder='Series Name'
              aria-label='Series name'
              value={name}
              disabled={isSaving}
              onChange={(e) => setName(e.target.value)} />
          </div>
        </div>

        <div className='section'>
          <div className='input labeled'>
            <label htmlFor='series-campus'>Campus</label>
            <select
              id='series-campus'
              value={campusID}
              disabled={isSaving}
              onChange={(e) => setCampusID(e.target.value)}>
              {campusID === '' ? <option value='' disabled>Choose a campus</option> : null}
              {sortedCampuses.map((campus) => (
                <option key={campus.id} value={campus.id}>{campus.attributes.name}</option>
              ))}
            </select>
          </div>

          <div className='separator' />

          <div className='input labeled'>
            <label htmlFor='series-category'>Category</label>
            <select
              id='series-category'
              value={categoryID}
              disabled={isSaving}
              onChange={(e) => setCategoryID(e.target.value)}>
              <option value=''>No Category</option>
              {sortedCategories.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>

          <div className='separator' />

          <div className='input labeled'>
            <label>Start Date</label>
            <div className='date-picker-container'>
              <DatePicker
                customInput={<DateButton />}
                selected={startDate}
                disabled={isSaving}
                dateFormat='PP'
                // Rendered at the top of the page, over the modal, rather than
                // inside it where it'd be clipped.
                withPortal
                portalId='series-start-date-portal'
                onChange={(date) => setStartDate(date)} />
            </div>
          </div>
        </div>

        <p className='footnote'>
          Series are listed newest first by their start date, usually the day of their first service.
          {isEditing ?
            ' Moving a series to another campus or category keeps its services in it.' :
            ' Add services to it from each service’s page, under Schedule.'}
        </p>

        <div className='section thumbnail-section'>
          {thumbnail ?
            <img className='thumbnail-preview' src={thumbnail.url} alt='Thumbnail preview' /> :
            <div className='thumbnail-preview empty' aria-hidden='true' />}

          <div className='thumbnail-controls'>
            <input
              ref={fileInputRef}
              type='file'
              accept='image/*'
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Cleared so picking the same file again still fires.
                e.target.value = '';

                if (file) {
                  void upload(file);
                }
              }} />

            <button
              type='button'
              className='secondary-button'
              disabled={isBusy}
              onClick={() => fileInputRef.current?.click()}>
              {isUploading ? 'Uploading...' : thumbnail ? 'Upload New Thumbnail' : 'Upload Thumbnail'}
            </button>

            {thumbnail && !isUploading ? <button
              type='button'
              className='text-button destructive'
              disabled={isSaving}
              onClick={() => setThumbnail(null)}>
              Remove Thumbnail
            </button> : null}
          </div>
        </div>

        <p className='footnote'>A 16:9 image works best. It’s shown wherever the series appears in Launchpad and in the Salt app.</p>

        <button type='submit' className='submit' disabled={!canSave}>
          {submitTitle}
        </button>
      </form>
    </div>
  </Modal>
}

// Sanctuary's refusal for a thumbnail hosted outside Salt, reworded for the
// person who hit it.
function friendlySaveError(error: string) {
  if (error.includes('Assets must be uploaded through the asset upload target')) {
    return "This series' thumbnail is hosted outside Salt, so it can't be saved as is. Upload the thumbnail again, or remove it, then save.";
  }

  return error;
}
